/**
 * Form primitives — the prototype's `.field`, `.input`, `.select`, `.textarea`, `.check` and
 * `.radio`, plus the field-level error state the prototype never defined.
 *
 * Every control is labelled, every error is announced (`aria-invalid` + `aria-describedby`), and
 * an error is text as well as colour.
 */
import {
  type ComponentPropsWithRef,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react';

interface FieldShellProps {
  label: ReactNode;
  help?: ReactNode | undefined;
  error?: string | null | undefined;
  required?: boolean | undefined;
  htmlFor: string;
  children: ReactNode;
  className?: string | undefined;
}

function FieldShell({
  label,
  help,
  error,
  required,
  htmlFor,
  children,
  className,
}: FieldShellProps): ReactElement {
  const helpId = `${htmlFor}-help`;
  const errorId = `${htmlFor}-error`;
  return (
    <div className={['field', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={htmlFor}>
        {label}
        {required ? (
          <>
            {' '}
            <span aria-hidden="true">*</span>
            <span className="sr-only">(zorunlu)</span>
          </>
        ) : null}
      </label>
      {children}
      {help ? (
        <span className="help" id={helpId}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function describedBy(id: string, help: unknown, error: unknown): string | undefined {
  const parts = [help ? `${id}-help` : '', error ? `${id}-error` : ''].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

export interface TextFieldProps extends Omit<ComponentPropsWithRef<'input'>, 'id'> {
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
  small?: boolean;
}

export function TextField({
  label,
  help,
  error,
  small,
  className,
  required,
  ...rest
}: TextFieldProps): ReactElement {
  const id = useId();
  return (
    <FieldShell
      label={label}
      help={help}
      error={error}
      required={required}
      htmlFor={id}
      className={className}
    >
      <input
        id={id}
        className={['input', small ? 'input-sm' : ''].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, help, error)}
        required={required}
        {...rest}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<ComponentPropsWithRef<'select'>, 'id'> {
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
  small?: boolean;
  children: ReactNode;
}

export function SelectField({
  label,
  help,
  error,
  small,
  className,
  required,
  children,
  ...rest
}: SelectFieldProps): ReactElement {
  const id = useId();
  return (
    <FieldShell
      label={label}
      help={help}
      error={error}
      required={required}
      htmlFor={id}
      className={className}
    >
      <select
        id={id}
        className={['select', small ? 'select-sm' : ''].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, help, error)}
        required={required}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends Omit<ComponentPropsWithRef<'textarea'>, 'id'> {
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
}

export function TextAreaField({
  label,
  help,
  error,
  className,
  required,
  ...rest
}: TextAreaFieldProps): ReactElement {
  const id = useId();
  return (
    <FieldShell
      label={label}
      help={help}
      error={error}
      required={required}
      htmlFor={id}
      className={className}
    >
      <textarea
        id={id}
        className="textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, help, error)}
        required={required}
        {...rest}
      />
    </FieldShell>
  );
}

export function Checkbox({
  label,
  ...rest
}: { label: ReactNode } & InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** Radio card (`.radio`), used for publication policy and similar exclusive choices. */
export function RadioCard({
  label,
  description,
  disabled,
  ...rest
}: {
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
} & InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return (
    <label className={['radio', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')}>
      <input type="radio" disabled={disabled} {...rest} />
      <span>
        <b>{label}</b>
        {description ? <span>{description}</span> : null}
      </span>
    </label>
  );
}

export function FieldRow({ children }: { children: ReactNode }): ReactElement {
  return <div className="field-row">{children}</div>;
}

export function Help({ children }: { children: ReactNode }): ReactElement {
  return <span className="help">{children}</span>;
}
